import {
    runThreeDSecureFlow,
    type StripeLike,
    type ThreeDSecureChallenge,
    type ThreeDSecureResult,
} from 'pb-billing-ui/attempt/three-d-secure.ts';

/**
 * How this app runs a bank step app-api asked for (`ThreeDSecureV3Resource`), on every screen that
 * takes a payment: the shared flow (`runThreeDSecureFlow`, `pb-billing-ui`) has the payer's bank
 * authenticate the charge through Stripe.js, watches a charge left processing until it settles or
 * `signal` ends the wait, and then calls `confirmPayment` for app-api to record the payment. What
 * `confirmPayment` resolves to comes back on the success result.
 *
 * One Stripe.js instance serves every bank step the composable's owner runs, a Pay all run's
 * included. A load that failed is not kept, so the next step tries again.
 */
export function useThreeDSecure() {
    const config = useRuntimeConfig();
    let stripe: Promise<StripeLike | null> | null = null;

    /**
     * Stripe.js for `key`, or null when it could not be loaded. A failed load is answered as null,
     * which the shared flow reports as authentication it could not start: no bank step ran, so
     * nothing was charged.
     */
    function stripeFor(key: string): Promise<StripeLike | null> {
        stripe ??= import('@stripe/stripe-js')
            .then(({ loadStripe }) => loadStripe(key))
            .catch(() => null)
            .then((instance) => {
                if (!instance) stripe = null;
                return instance;
            });

        return stripe;
    }

    return async function authenticate<Confirmed>(
        challenge: ThreeDSecureChallenge,
        confirmPayment: () => Promise<Confirmed>,
        signal?: AbortSignal,
    ): Promise<ThreeDSecureResult<Confirmed>> {
        const clientSecret = challenge.payment_intent_client_secret;
        if (!clientSecret) {
            return { ok: false, charged: false, message: "We couldn't start your bank's verification for this card." };
        }

        return runThreeDSecureFlow(
            { payment_intent_client_secret: clientSecret, invoice_id: challenge.invoice_id },
            {
                // The key this app's card forms create cards with, so the bank step runs against the
                // Stripe account the card was made in.
                stripeKey: config.public.pb.stripeKey,
                loadStripe: stripeFor,
                confirmPayment,
                signal,
            },
        );
    };
}
